import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { CaseworkerAbsenceModel } from '../../../../api-service/clients';
import { formatDate } from '../../../../utilities/date-helper';
import { forumRejectionReason } from '../forum-rejection-reason';
import { useForumCaseworkerContext } from '../use-forum-caseworker-context';
import { isActiveOrPlanned } from './absence-day';
import { IAbsenceForm } from './caseworker-absence-form';
import { ForumSettingsTabComponent, ICaseworkerOption } from './forum-settings-tab-component';

const client = moduleClientFactory.createForumCaseworkerClient();

const isoDateFormat = 'yyyy-MM-dd';

export function ForumSettingsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const { canManageAbsenceOfOtherCaseworkers } = useForumCaseworkerContext();
  const [caseworkers, setCaseworkers] = useState<ICaseworkerOption[]>([]);
  const [selectedCaseworkerId, setSelectedCaseworkerId] = useState('');
  const latestAbsencesRequest = useRef(0);
  const [absences, setAbsences] = useState<CaseworkerAbsenceModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const [absenceToEdit, setAbsenceToEdit] = useState<CaseworkerAbsenceModel>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedAbsenceIds, setSelectedAbsenceIds] = useState<string[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  const caseworkerId = selectedCaseworkerId || undefined;
  const isBusy = isSaving || isDeleting;

  useEffect(() => {
    setIsLoading(true);
    setAbsences([]);
    setSelectedAbsenceIds([]);
    loadAbsences();
  }, [selectedCaseworkerId]);

  useEffect(() => {
    if (!canManageAbsenceOfOtherCaseworkers) {
      return;
    }
    client
      .getForumCaseworkers()
      .then(result =>
        setCaseworkers((result.caseworkers ?? []).map(caseworker => ({ id: caseworker.id, name: caseworker.name ?? '' })).sort((a, b) => a.name.localeCompare(b.name, 'da')))
      )
      .catch(() => {
        setCaseworkers([]);
        enqueueSnackbar('Kan ikke hente sagsbehandlere.', { variant: 'error' });
      });
  }, [canManageAbsenceOfOtherCaseworkers]);

  async function loadAbsences() {
    const request = ++latestAbsencesRequest.current;
    try {
      const result = await client.getCaseworkerAbsences({ caseworkerId });
      if (request !== latestAbsencesRequest.current) {
        return;
      }
      const loadedAbsences = result.absences ?? [];
      setAbsences(loadedAbsences);
      const visibleIds = new Set(loadedAbsences.filter(isActiveOrPlanned).map(absence => absence.absenceId));
      setSelectedAbsenceIds(ids => ids.filter(id => visibleIds.has(id)));
      setHasFailed(false);
    } catch {
      if (request !== latestAbsencesRequest.current) {
        return;
      }
      setAbsences([]);
      setHasFailed(true);
      enqueueSnackbar('Kan ikke hente fravær.', { variant: 'error' });
    } finally {
      if (request === latestAbsencesRequest.current) {
        setIsLoading(false);
      }
    }
  }

  function handleCaseworkerChange(id: string) {
    setSelectedCaseworkerId(id);
    closeForm();
  }

  function openForm(absence?: CaseworkerAbsenceModel) {
    setAbsenceToEdit(absence);
    setSaveError(undefined);
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
  }

  async function handleSave(form: IAbsenceForm) {
    const request = {
      caseworkerId,
      startDate: formatDate(form.startDate, isoDateFormat),
      endDate: form.endDate ? formatDate(form.endDate, isoDateFormat) : undefined,
      text: form.text.trim(),
    };
    setIsSaving(true);
    setSaveError(undefined);
    try {
      if (absenceToEdit) {
        await client.updateCaseworkerAbsence({ absenceId: absenceToEdit.absenceId, ...request });
      } else {
        await client.createCaseworkerAbsence(request);
      }
      enqueueSnackbar('Fraværet er gemt.', { variant: 'success' });
      closeForm();
      await loadAbsences();
    } catch (error) {
      setSaveError(forumRejectionReason(error) ?? 'Fraværet kunne ikke gemmes.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancelForm() {
    if (!isBusy) {
      closeForm();
    }
  }

  function handleCreateAbsence() {
    if (!isBusy) {
      openForm();
    }
  }

  function handleOpenAbsence(absence: CaseworkerAbsenceModel) {
    if (!isBusy) {
      openForm(absence);
    }
  }

  function handleSelectionChange(absenceIds: string[]) {
    if (!isBusy) {
      setSelectedAbsenceIds(absenceIds);
    }
  }

  function handleRequestDelete() {
    if (!isBusy) {
      setIsDeleteDialogOpen(true);
    }
  }

  function handleDeclineDelete() {
    setIsDeleteDialogOpen(false);
  }

  async function handleConfirmDelete() {
    setIsDeleteDialogOpen(false);
    setIsDeleting(true);
    const idsToDelete = selectedAbsenceIds;
    const failedIds: string[] = [];
    try {
      for (const absenceId of idsToDelete) {
        try {
          await client.deleteCaseworkerAbsence({ absenceId, caseworkerId });
        } catch {
          failedIds.push(absenceId);
        }
      }
      setSelectedAbsenceIds(failedIds);
      if (failedIds.length > 0) {
        enqueueSnackbar(failedIds.length === 1 ? 'Et fravær kunne ikke slettes.' : `${failedIds.length} fravær kunne ikke slettes.`, { variant: 'error' });
      } else {
        enqueueSnackbar(idsToDelete.length === 1 ? 'Fraværet er slettet.' : 'Fraværene er slettet.', { variant: 'success' });
      }
      await loadAbsences();
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <ForumSettingsTabComponent
      caseworkers={caseworkers}
      selectedCaseworkerId={selectedCaseworkerId}
      showCaseworkerPicker={canManageAbsenceOfOtherCaseworkers}
      onCaseworkerChange={handleCaseworkerChange}
      absences={absences}
      isLoading={isLoading}
      hasFailed={hasFailed}
      isFormOpen={isFormOpen}
      absenceToEdit={absenceToEdit}
      selectedAbsenceIds={selectedAbsenceIds}
      isDeleteDialogOpen={isDeleteDialogOpen}
      isBusy={isBusy}
      saveError={saveError}
      onSave={handleSave}
      onCancelForm={handleCancelForm}
      onCreateAbsence={handleCreateAbsence}
      onOpenAbsence={handleOpenAbsence}
      onSelectionChange={handleSelectionChange}
      onRequestDelete={handleRequestDelete}
      onConfirmDelete={handleConfirmDelete}
      onDeclineDelete={handleDeclineDelete}
    />
  );
}
